import { NOTIFICATION_TYPE } from '../../../../common/constant/constant';
import type { AnnotationLabelT } from '../../../../common/types/types';
import type { RangeSpanT, ShowNotificationT } from '../../../../common/types/types';
import type { BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT, SAMSegmentVideoData } from '../../../types';
import { applyColorToImageData, applyOpacityToImageData, base64ToImageData, imageDataToBase64String, mergeBitmaps, preProcessSAMPixelData } from '../../components/annotations/canvas/utils';
import { BBOX_TRACKING_WARNING_MESSAGE } from '../../components/frames-control-panel/constants';
import { mapBoundingBoxTrackingDataToFrameAnnotations, mapToTrackingInput } from '../../components/frames-control-panel/frames-timeline/utils';
import { ANNOTATION_TYPE, SEGMENTATION_ALPHA } from '../../constants';
import type { AnnotationT } from '../../types';
export const processSAMTrackingChunk = async (samDataChunk: SAMSegmentVideoData, annotationLabel: AnnotationLabelT, startFrame: number, chunkStartIndex: number, existingBitmaps: {
  [frameIndex: number]: {
    [labelTitle: string]: ImageData;
  };
} = {}, sourceAnnotation?: AnnotationT): Promise<{
  bitmapUpdates: {
    [frameIndex: number]: ImageData;
  };
  annotations: {
    [frameIndex: number]: any;
  };
}> => {
  const bitmapUpdates: {
    [frameIndex: number]: ImageData;
  } = {};
  const annotations: {
    [frameIndex: number]: AnnotationT;
  } = {};
  const processedResults = await Promise.allSettled(samDataChunk.map(async (segmentData, chunkIndex) => {
    const currentFrame = startFrame + chunkStartIndex + chunkIndex;
    const currentBitmap = await base64ToImageData(segmentData.mask, preProcessSAMPixelData);
    const existingBitmap = existingBitmaps[currentFrame]?.[annotationLabel.title];
    let mergedBitmap: ImageData | null = currentBitmap;
    if (existingBitmap && currentBitmap) {
      applyOpacityToImageData(currentBitmap, SEGMENTATION_ALPHA);
      applyColorToImageData(currentBitmap, annotationLabel.style.strokeColor);
      mergedBitmap = mergeBitmaps(existingBitmap, currentBitmap, annotationLabel.style.strokeColor).mergedBitmap;
    }
    if (mergedBitmap) {
      const base64String = imageDataToBase64String(mergedBitmap);
      return {
        currentFrame,
        bitmap: mergedBitmap,
        annotation: {
          annotationType: ('SEGMENTATION' as const),
          data: {
            base64String,
            color: annotationLabel.style.strokeColor
          },
          annotationLabel,
          ...(sourceAnnotation?.metadata && {
            metadata: sourceAnnotation.metadata
          })
        }
      };
    }
    return null;
  }));
  processedResults.forEach(result => {
    if (result.status === 'fulfilled' && result.value) {
      const {
        currentFrame,
        bitmap,
        annotation
      } = result.value;
      bitmapUpdates[currentFrame] = bitmap;
      annotations[currentFrame] = annotation;
    }
  });
  return {
    bitmapUpdates,
    annotations
  };
};
export const handleAnnotationTracking = async ({
  state,
  actions,
  fetchAnnotationTrackingData,
  showNotification
}: {
  state: {
    annotationId: string;
    annotation: AnnotationT;
    annotationSequence: Map<string, RangeSpanT>;
  };
  actions: {
    setLoading: (loading: boolean) => void;
    updateBitmapsOnTrack: (samTrackingData: SAMSegmentVideoData, annotationLabel: AnnotationLabelT, startFrame: number, sourceAnnotation?: AnnotationT) => Promise<{
      [frameIndex: number]: AnnotationT;
    }>;
    updateTrackingFrames: (annotationId: string, startFrame: number, endFrame: number, updatedAnnotationsArray: {
      [frameIndex: number]: AnnotationT;
    }) => void;
    setCurrentFrame: (frame: number) => void;
  };
  fetchAnnotationTrackingData?: (labelTitle: string, frameRange?: {
    start: number;
    end: number;
  }) => Promise<SAMSegmentVideoData | Error>;
  showNotification?: ShowNotificationT;
}): Promise<RangeSpanT | null> => {
  actions.setLoading(true);
  const sequenceEntries = Array.from(state.annotationSequence.values());
  const lastSequence = sequenceEntries[sequenceEntries.length - 1];
  if (!lastSequence) {
    showNotification?.(NOTIFICATION_TYPE.ERROR, 'No annotation sequence found');
    actions.setLoading(false);
    return null;
  }
  const samTrackingData = await fetchAnnotationTrackingData?.(state.annotation.annotationLabel.title, {
    start: lastSequence.start,
    end: lastSequence.end
  });
  if (samTrackingData instanceof Error) {
    showNotification?.(NOTIFICATION_TYPE.ERROR, samTrackingData.message);
    actions.setLoading(false);
    return null;
  }
  if (!samTrackingData || samTrackingData.length === 0) {
    showNotification?.(NOTIFICATION_TYPE.WARNING, 'No tracking data found');
    actions.setLoading(false);
    return null;
  }
  const updatedAnnotationsArray = await actions.updateBitmapsOnTrack(samTrackingData, state.annotation.annotationLabel, lastSequence.start, state.annotation);
  actions.updateTrackingFrames(state.annotationId, lastSequence.start, lastSequence.end, updatedAnnotationsArray);
  showNotification?.(NOTIFICATION_TYPE.SUCCESS, `Tracking successful for the given range: ${lastSequence.start + 1} - ${lastSequence.end + 1}`);
  actions.setLoading(false);
  return lastSequence;
};
export const handleBoundingBoxTracking = async ({
  state,
  actions,
  getBoundingBoxTrackingData,
  showNotification
}: {
  state: {
    annotationId: string;
    annotation: AnnotationT;
    annotationSequence: Map<string, RangeSpanT>;
  };
  actions: {
    setLoading: (loading: boolean) => void;
    updateTrackingFrames: (annotationId: string, startFrame: number, endFrame: number, updatedAnnotationsArray: {
      [frameIndex: number]: AnnotationT;
    }) => void;
    setCurrentFrame: (frame: number) => void;
  };
  getBoundingBoxTrackingData?: (frameIndex: number, bbox: BoundingBoxTrackingInputT['bbox'], numFramesToTrack: number) => Promise<BoundingBoxTrackingOutputT | Error>;
  showNotification?: ShowNotificationT;
}): Promise<RangeSpanT | null> => {
  const sequenceEntries = Array.from(state.annotationSequence.values());
  const lastSequence = sequenceEntries[sequenceEntries.length - 1];
  actions.setLoading(true);
  showNotification?.(NOTIFICATION_TYPE.INFO,
  // @ts-expect-error todo(ts-migration) TS18048 'lastSequence' is possibly 'undefined'.
  `Starting bounding box tracking for the given range: ${lastSequence.start + 1} - ${
  // @ts-expect-error todo(ts-migration) TS18048 'lastSequence' is possibly 'undefined'.
  lastSequence.end + 1}`);
  if (!lastSequence) {
    showNotification?.(NOTIFICATION_TYPE.ERROR, 'No annotation sequence found');
    actions.setLoading(false);
    return null;
  }
  const currentFrame = lastSequence.start - 1;
  if (!state.annotation.data || state.annotation.annotationType !== ANNOTATION_TYPE.RECTANGLE) {
    showNotification?.(NOTIFICATION_TYPE.WARNING, `${BBOX_TRACKING_WARNING_MESSAGE} ${lastSequence.start} - ${lastSequence.end}`);
    actions.setLoading(false);
    return null;
  }
  const currentFrameBoundingBox = mapToTrackingInput(state.annotation.data);
  const boundingBoxTrackingData = await getBoundingBoxTrackingData?.(currentFrame, currentFrameBoundingBox, lastSequence.end - lastSequence.start + 1);
  if (!boundingBoxTrackingData || boundingBoxTrackingData instanceof Error) {
    const errorMessage = boundingBoxTrackingData?.message || 'Error fetching bounding box tracking data';
    showNotification?.(NOTIFICATION_TYPE.ERROR, errorMessage);
    actions.setLoading(false);
    return null;
  }
  const annotationLabel = state.annotation.annotationLabel;
  const frameAnnotations = mapBoundingBoxTrackingDataToFrameAnnotations(boundingBoxTrackingData, lastSequence.start, annotationLabel, showNotification, state.annotation);
  actions.updateTrackingFrames(state.annotationId, lastSequence.start, lastSequence.end, frameAnnotations);
  showNotification?.(NOTIFICATION_TYPE.INFO, `Bounding box tracking successful for the given range: ${lastSequence.start + 1} - ${lastSequence.end + 1}`);
  actions.setLoading(false);
  return lastSequence;
};